import React, { useState } from 'react';
import {
  X,
  Plug,
  Check,
  Info,
  Server
} from 'lucide-react';

const CONNECTORS_LIST = [
  { id: 'nx', name: 'Siemens NX', desc: 'CAD / CAM', mono: 'NX', color: '#008A8A' },
  { id: 'creo', name: 'PTC Creo', desc: 'Parametric CAD', mono: 'PC', color: '#4A4A4A' },
  { id: 'catia', name: 'CATIA', desc: '3DEXPERIENCE', mono: 'CA', color: '#0C5A8E' },
  { id: 'ansys', name: 'Ansys', desc: 'Simulation', mono: 'AN', color: '#C77F00' }
];

export default function ConnectorsModal({ isOpen, onClose }) {
  const [connectedTools, setConnectedTools] = useState(new Set());
  const [isMcpConnected, setIsMcpConnected] = useState(false);

  if (!isOpen) return null;

  const toggleConnect = (id) => {
    setConnectedTools((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-[#102442]/45 backdrop-blur-xs z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[440px] max-w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="flex items-start gap-3.5 p-5 pb-3.5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-[#EFF5FE] text-[#0C64E8] flex items-center justify-center shrink-0">
            <Plug className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 leading-tight">Connectors</h3>
            <p className="text-xs text-slate-500 mt-1 leading-snug">
              Link your engineering tools so Dashnode works from your real data.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 pt-3.5 space-y-3">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400 block">
            Connect a tool
          </span>

          <div className="divide-y divide-slate-100">
            {CONNECTORS_LIST.map((tool) => {
              const isConnected = connectedTools.has(tool.id);
              return (
                <div key={tool.id} className="flex items-center gap-3 py-2.5">
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white shrink-0 tracking-tight"
                    style={{ backgroundColor: tool.color }}
                  >
                    {tool.mono}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold text-slate-900 block truncate">
                      {tool.name}
                    </span>
                    <span className="text-[11px] text-slate-400 block truncate">
                      {tool.desc}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleConnect(tool.id)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                      isConnected
                        ? 'bg-[#E4F5EF] text-[#0C8A74] border border-[#E4F5EF]'
                        : 'border border-slate-200 text-[#0C64E8] hover:bg-[#EFF5FE]'
                    }`}
                  >
                    {isConnected ? (
                      <>
                        <Check className="w-3 h-3 stroke-[2.5]" />
                        Connected
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* MCP Server Card */}
          <div className="border border-dashed border-[#C6D6EE] bg-[#EFF5FE]/70 rounded-xl p-3.5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white text-[#0C64E8] flex items-center justify-center shrink-0 shadow-2xs">
              <Server className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <b className="text-xs font-semibold text-slate-900 block">
                Connect any tool via MCP
              </b>
              <span className="text-[11px] text-slate-500 block leading-tight">
                Point Dashnode at an MCP server for anything not listed.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsMcpConnected(!isMcpConnected)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all shrink-0 inline-flex items-center gap-1.5 ${
                isMcpConnected
                  ? 'bg-[#E4F5EF] text-[#0C8A74] border border-[#E4F5EF]'
                  : 'border border-slate-200 bg-white text-[#0C64E8] hover:bg-[#EFF5FE]'
              }`}
            >
              {isMcpConnected ? (
                <>
                  <Check className="w-3 h-3 stroke-[2.5]" />
                  Connected
                </>
              ) : (
                'Connect'
              )}
            </button>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-slate-400 pt-1">
            <Info className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>Sample connectors — this prototype does not link external servers.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
